import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeesAPI, departmentsAPI, contractsAPI } from '../lib/api';
import { formatDate, getStatusColor, cn, getInitials } from '../lib/utils';
import { Search, Plus, MoreVertical, Eye, Edit, Trash2, UserX, AlertTriangle, Clock, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const employmentTypes = ['Permanent', 'Temporary', 'Fixed-Term Contract', 'Contractor', 'Subcontractor', 'Casual/Seasonal'];

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [contractStats, setContractStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterContractExpiry, setFilterContractExpiry] = useState('all');
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [terminationData, setTerminationData] = useState({
    termination_reason: '',
    termination_date: new Date().toISOString().split('T')[0],
    final_pay_processed: false,
    exit_interview_completed: false,
  });
  const navigate = useNavigate();
  const { isAdmin, isHRManager } = useAuth();

  useEffect(() => {
    fetchData();
  }, [filterDept, filterStatus, searchTerm, filterType]);

  const fetchData = async () => {
    try {
      // Use allSettled so a failure in ONE sub-endpoint (e.g. contracts/stats)
      // doesn't block the main employees list from rendering.
      const [empRes, deptRes, statsRes] = await Promise.allSettled([
        employeesAPI.getAll({
          department: filterDept !== 'all' ? filterDept : undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          search: searchTerm || undefined,
        }),
        departmentsAPI.getAll(),
        contractsAPI.getStats(),
      ]);

      if (empRes.status === 'rejected') {
        const detail = empRes.reason?.response?.data?.detail
          || empRes.reason?.message
          || 'Failed to load employees';
        toast.error(`Failed to load employees: ${detail}`);
        console.error('GET /employees failed', empRes.reason);
        return;
      }

      let filteredEmployees = empRes.value.data;

      // Filter by employment type
      if (filterType !== 'all') {
        filteredEmployees = filteredEmployees.filter(e => e.employment_type === filterType);
      }

      // Filter by contract expiry
      if (filterContractExpiry !== 'all') {
        const today = new Date();
        filteredEmployees = filteredEmployees.filter(e => {
          if (!e.contract_end_date) return false;
          const endDate = new Date(e.contract_end_date);
          const daysUntil = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

          switch (filterContractExpiry) {
            case 'expired': return daysUntil < 0;
            case '7days': return daysUntil >= 0 && daysUntil <= 7;
            case '14days': return daysUntil >= 0 && daysUntil <= 14;
            case '30days': return daysUntil >= 0 && daysUntil <= 30;
            case 'this_month':
              const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
              return endDate >= today && endDate <= monthEnd;
            default: return true;
          }
        });
      }

      setEmployees(filteredEmployees);

      if (deptRes.status === 'fulfilled') {
        setDepartments(deptRes.value.data);
      } else {
        console.warn('GET /departments failed', deptRes.reason);
      }

      if (statsRes.status === 'fulfilled') {
        setContractStats(statsRes.value.data);
      } else {
        console.warn('GET /contracts/stats failed', statsRes.reason);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unexpected error';
      toast.error(`Failed to load employees: ${detail}`);
      console.error('fetchData error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to permanently delete this employee?')) return;
    
    try {
      await employeesAPI.delete(employeeId);
      toast.success('Employee deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleTerminate = async () => {
    if (!terminationData.termination_reason) {
      toast.error('Please select a termination reason');
      return;
    }

    try {
      await employeesAPI.terminate(selectedEmployee.employee_id, terminationData);
      toast.success('Employee terminated');
      setShowTerminateModal(false);
      setSelectedEmployee(null);
      setTerminationData({
        termination_reason: '',
        termination_date: new Date().toISOString().split('T')[0],
        final_pay_processed: false,
        exit_interview_completed: false,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate employee');
    }
  };

  const getDaysToExpiry = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const end = new Date(endDate);
    return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadge = (employee) => {
    if (employee.employment_type === 'Permanent') return null;
    if (!employee.contract_end_date) return null;
    
    const days = getDaysToExpiry(employee.contract_end_date);
    
    if (days < 0) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 text-xs ml-2">Expired</Badge>;
    } else if (days <= 7) {
      return <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 text-xs ml-2">{days}d</Badge>;
    } else if (days <= 14) {
      return <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs ml-2">{days}d</Badge>;
    } else if (days <= 30) {
      return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs ml-2">{days}d</Badge>;
    }
    return null;
  };

  const getEmploymentTypeBadge = (type) => {
    const colors = {
      'Permanent': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Temporary': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Fixed-Term Contract': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Contractor': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      'Subcontractor': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Casual/Seasonal': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    };
    return colors[type] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  return (
    <div className="space-y-6 page-transition" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Employees</h1>
          <p className="text-muted-foreground mt-1">Manage your organization's workforce</p>
        </div>
        {isHRManager() && (
          <Button onClick={() => navigate('/employees/new')} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        )}
      </div>

      {/* Contract Stats Cards */}
      {contractStats && (contractStats.expired_contracts > 0 || contractStats.expiring_in_7_days > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {contractStats.expired_contracts > 0 && (
            <div className="stat-tile border-red-500/30 bg-red-500/5">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-bold text-2xl">{contractStats.expired_contracts}</span>
              </div>
              <p className="text-sm text-red-400/80 mt-1">Expired Contracts</p>
            </div>
          )}
          {contractStats.expiring_in_7_days > 0 && (
            <div className="stat-tile border-amber-500/30 bg-amber-500/5">
              <div className="flex items-center gap-2 text-amber-400">
                <Clock className="w-5 h-5" />
                <span className="font-bold text-2xl">{contractStats.expiring_in_7_days}</span>
              </div>
              <p className="text-sm text-amber-400/80 mt-1">Expiring in 7 days</p>
            </div>
          )}
          {contractStats.expiring_in_14_days > contractStats.expiring_in_7_days && (
            <div className="stat-tile border-yellow-500/30 bg-yellow-500/5">
              <div className="flex items-center gap-2 text-yellow-400">
                <Calendar className="w-5 h-5" />
                <span className="font-bold text-2xl">{contractStats.expiring_in_14_days}</span>
              </div>
              <p className="text-sm text-yellow-400/80 mt-1">Expiring in 14 days</p>
            </div>
          )}
          <div className="stat-tile">
            <div className="flex items-center gap-2 text-primary">
              <span className="font-bold text-2xl">{contractStats.total_contract_employees}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Contract Employees</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-employees"
            />
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-full lg:w-40" data-testid="filter-department">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Depts</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full lg:w-44" data-testid="filter-employment-type">
              <SelectValue placeholder="Employment Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {employmentTypes.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full lg:w-36" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Pending Start">Pending Start</SelectItem>
              <SelectItem value="Expiring Soon">Expiring Soon</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
              <SelectItem value="Terminated">Terminated</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterContractExpiry} onValueChange={setFilterContractExpiry}>
            <SelectTrigger className="w-full lg:w-40" data-testid="filter-contract-expiry">
              <SelectValue placeholder="Contract Expiry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Contracts</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="7days">Next 7 days</SelectItem>
              <SelectItem value="14days">Next 14 days</SelectItem>
              <SelectItem value="30days">Next 30 days</SelectItem>
              <SelectItem value="this_month">This month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="glass-card p-6 text-center text-muted-foreground">Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="glass-card p-6 text-center text-muted-foreground">No employees found</div>
        ) : (
          employees.map((employee) => {
            const daysToExpiry = getDaysToExpiry(employee.contract_end_date);
            const isExpired = daysToExpiry !== null && daysToExpiry < 0;
            const isExpiringSoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 7;
            return (
              <div
                key={employee.employee_id}
                onClick={() => navigate(`/employees/${employee.employee_id}`)}
                data-testid={`employee-card-${employee.employee_id}`}
                className={cn(
                  "glass-card p-4 space-y-3 transition-colors",
                  isExpired && "border-red-500/20 bg-red-500/5",
                  isExpiringSoon && "border-amber-500/20 bg-amber-500/5"
                )}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="flex-shrink-0">
                    <AvatarImage src={employee.avatar} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {getInitials(employee.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{employee.full_name}</p>
                    <p className="text-sm text-muted-foreground truncate">{employee.job_title}</p>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`employee-actions-m-${employee.employee_id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/employees/${employee.employee_id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                        {isHRManager() && employee.status !== 'Terminated' && (
                          <DropdownMenuItem onClick={() => navigate(`/employees/${employee.employee_id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {isAdmin() && employee.status !== 'Terminated' && (
                          <DropdownMenuItem
                            onClick={() => { setSelectedEmployee(employee); setShowTerminateModal(true); }}
                            className="text-amber-500"
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Terminate
                          </DropdownMenuItem>
                        )}
                        {isAdmin() && (
                          <DropdownMenuItem onClick={() => handleDelete(employee.employee_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={cn("border text-xs", getEmploymentTypeBadge(employee.employment_type))}>
                    {employee.employment_type}
                  </Badge>
                  <Badge variant="outline" className={cn("border", getStatusColor(employee.status))}>
                    {employee.status}
                  </Badge>
                  {getExpiryBadge(employee)}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{employee.department}</span>
                  {daysToExpiry !== null && (
                    <span className={cn(
                      "font-medium",
                      daysToExpiry < 0 && "text-red-400",
                      daysToExpiry >= 0 && daysToExpiry <= 7 && "text-amber-400",
                      daysToExpiry > 7 && daysToExpiry <= 30 && "text-yellow-400"
                    )}>
                      {daysToExpiry < 0 ? `${Math.abs(daysToExpiry)} overdue` : `${daysToExpiry} days`}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Table (tablet / desktop) */}
      <div className="hidden md:block glass-card table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[250px]">Employee</TableHead>
              <TableHead>Employment Type</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Contract End</TableHead>
              <TableHead>Days Left</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading employees...
                </TableCell>
              </TableRow>
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No employees found
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => {
                const daysToExpiry = getDaysToExpiry(employee.contract_end_date);
                const isExpired = daysToExpiry !== null && daysToExpiry < 0;
                const isExpiringSoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 7;
                
                return (
                  <TableRow 
                    key={employee.employee_id} 
                    className={cn(
                      "table-row-hover cursor-pointer",
                      isExpired && "bg-red-500/5",
                      isExpiringSoon && "bg-amber-500/5"
                    )}
                    onClick={() => navigate(`/employees/${employee.employee_id}`)}
                    data-testid={`employee-row-${employee.employee_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={employee.avatar} />
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {getInitials(employee.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">{employee.full_name}</p>
                          <p className="text-sm text-muted-foreground">{employee.job_title}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("border text-xs", getEmploymentTypeBadge(employee.employment_type))}>
                        {employee.employment_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{employee.department}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("border", getStatusColor(employee.status))}>
                        {employee.status}
                      </Badge>
                      {getExpiryBadge(employee)}
                    </TableCell>
                    <TableCell className={cn(
                      "text-muted-foreground",
                      isExpired && "text-red-400 font-medium",
                      isExpiringSoon && "text-amber-400 font-medium"
                    )}>
                      {employee.employment_type !== 'Permanent' && employee.contract_end_date 
                        ? formatDate(employee.contract_end_date) 
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {daysToExpiry !== null ? (
                        <span className={cn(
                          "font-medium",
                          daysToExpiry < 0 && "text-red-400",
                          daysToExpiry >= 0 && daysToExpiry <= 7 && "text-amber-400",
                          daysToExpiry > 7 && daysToExpiry <= 30 && "text-yellow-400"
                        )}>
                          {daysToExpiry < 0 ? `${Math.abs(daysToExpiry)} overdue` : `${daysToExpiry} days`}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`employee-actions-${employee.employee_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/employees/${employee.employee_id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          {isHRManager() && employee.status !== 'Terminated' && (
                            <DropdownMenuItem onClick={() => navigate(`/employees/${employee.employee_id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {isAdmin() && employee.status !== 'Terminated' && (
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setShowTerminateModal(true);
                              }}
                              className="text-amber-500"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Terminate
                            </DropdownMenuItem>
                          )}
                          {isAdmin() && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(employee.employee_id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Termination Modal */}
      <Dialog open={showTerminateModal} onOpenChange={setShowTerminateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Terminate Employee</DialogTitle>
            <DialogDescription>
              You are about to terminate {selectedEmployee?.full_name}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Termination Reason</Label>
              <Select 
                value={terminationData.termination_reason} 
                onValueChange={(v) => setTerminationData({...terminationData, termination_reason: v})}
              >
                <SelectTrigger data-testid="termination-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resignation">Resignation</SelectItem>
                  <SelectItem value="Dismissal">Dismissal</SelectItem>
                  <SelectItem value="Redundancy">Redundancy</SelectItem>
                  <SelectItem value="End of Contract">End of Contract</SelectItem>
                  <SelectItem value="Retirement">Retirement</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Termination Date</Label>
              <Input
                type="date"
                value={terminationData.termination_date}
                onChange={(e) => setTerminationData({...terminationData, termination_date: e.target.value})}
                data-testid="termination-date"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="final_pay"
                checked={terminationData.final_pay_processed}
                onCheckedChange={(v) => setTerminationData({...terminationData, final_pay_processed: v})}
              />
              <Label htmlFor="final_pay">Final pay processed</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="exit_interview"
                checked={terminationData.exit_interview_completed}
                onCheckedChange={(v) => setTerminationData({...terminationData, exit_interview_completed: v})}
              />
              <Label htmlFor="exit_interview">Exit interview completed</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTerminateModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleTerminate} data-testid="confirm-terminate-btn">
              Terminate Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
